import { Gauge, Pause, Play, SkipBack, SkipForward, X } from "lucide-react";
import { useSyncExternalStore, type CSSProperties } from "react";
import { closeReadAloud, RATES, type ReadAloudPanel } from "../editor/readaloud";
import { collectDefinitions, type SpeechContext } from "../editor/speech";
import type { ProjectIndex } from "../lib/api";
import { keys } from "./MenuBar";
import "./readaloud.css";

const REGIONS: Record<string, string> = {
  AU: "Australian", GB: "British", US: "American", NZ: "New Zealand", IE: "Irish", IN: "Indian", ZA: "South African", SG: "Singaporean", CA: "Canadian", SC: "Scottish",
};

function voiceLabel(v: SpeechSynthesisVoice): string {
  const region = REGIONS[v.lang.replace("_", "-").split("-")[1]?.toUpperCase() ?? ""];
  // Chrome names some voices "Karen (English (Australia))"; the region says that already.
  const name = v.name.replace(/\s*\((English|en)[^)]*\)+$/i, "").replace(/^(Microsoft|Google)\s+/, "");
  return region && !name.includes(region) ? `${name} · ${region}` : name;
}

function timeLeft(minutes: number): string {
  if (minutes < 1) return "under a minute left";
  if (minutes < 60) return `about ${Math.round(minutes)} min left`;
  const h = Math.floor(minutes / 60);
  return `about ${h} h ${Math.round(minutes - h * 60)} min left`;
}

/** The read-aloud player along the bottom of the editor, rendered into CodeMirror's panel. */
export function ReadAloudBar({ panel }: { panel: ReadAloudPanel }) {
  useSyncExternalStore(panel.subscribe, panel.getVersion);
  const { status, view } = panel;
  const playing = status === "playing" || status === "loading";
  const reading = panel.index >= 0;
  const progress = panel.progress();
  const voice = panel.voice();
  const local = panel.voices.filter((v) => v.localService);
  const online = panel.voices.filter((v) => !v.localService);

  let label: string;
  let detail = "";
  if (!panel.supported) label = "This browser can't read aloud.";
  else if (status === "loading") label = "Getting ready…";
  else if (status === "playing") {
    label = "Reading";
    detail = `Sentence ${progress.sentence} of ${progress.total} · ${timeLeft(progress.minutesLeft)}`;
  } else if (status === "paused") {
    label = panel.message ?? "Paused";
    detail = "Play carries on, or click where to start";
  } else if (status === "done") {
    label = panel.message ?? "Finished";
    detail = "Play reads from the cursor again";
  } else {
    label = "Read aloud";
    detail = "Reads from the cursor, or just the selection";
  }

  const refocus = () => view.focus();
  const button = (e: React.MouseEvent) => e.preventDefault(); // keep the cursor in the editor

  return (
    <div
      className={`ra-bar ra-${status}${panel.message ? " ra-problem" : ""}`}
      role="region"
      aria-label="Read aloud"
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        closeReadAloud(view);
        view.focus();
      }}
    >
      {reading && <div className="ra-progress" style={{ "--p": progress.total ? progress.sentence / progress.total : 0 } as CSSProperties} />}
      <div className="ra-controls">
        <button type="button" className="icon-btn ra-skip" title="Previous sentence" aria-label="Previous sentence" disabled={!reading} onMouseDown={button} onClick={() => panel.prev()}>
          <SkipBack size={15} />
        </button>
        <button
          type="button"
          className="ra-play"
          title={`${playing ? "Pause" : "Play"} (${keys("Mod-Shift-u")})`}
          aria-label={playing ? "Pause" : "Play"}
          disabled={!panel.supported}
          onMouseDown={button}
          onClick={() => panel.toggle()}
        >
          {playing ? <Pause size={15} fill="currentColor" strokeWidth={0} /> : <Play size={15} fill="currentColor" strokeWidth={0} className="ra-play-icon" />}
        </button>
        <button type="button" className="icon-btn ra-skip" title="Next sentence" aria-label="Next sentence" disabled={!reading} onMouseDown={button} onClick={() => panel.next()}>
          <SkipForward size={15} />
        </button>
      </div>
      <div className="ra-status" aria-live="polite">
        <span className="ra-wave" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
        <span className="ra-label">{label}</span>
        {detail && <span className="ra-detail">{detail}</span>}
      </div>
      {panel.supported && (
        <>
          <label className="ra-select ra-rate" title="Speed">
            <Gauge size={14} aria-hidden="true" />
            <select
              aria-label="Speed"
              value={panel.rate}
              onChange={(e) => {
                panel.setRate(Number(e.target.value));
                refocus();
              }}
            >
              {RATES.map((r) => (
                <option key={r} value={r}>
                  {r}×
                </option>
              ))}
            </select>
          </label>
          <label className="ra-select ra-voice" title={voice && !voice.localService ? "Online voices send the text to their provider to be spoken" : "Voice"}>
            <select
              aria-label="Voice"
              value={voice?.voiceURI ?? ""}
              disabled={!panel.voices.length}
              onChange={(e) => {
                panel.setVoice(e.target.value);
                refocus();
              }}
            >
              {!panel.voices.length && <option value="">No voices yet</option>}
              {local.length > 0 && (
                <optgroup label="On this device">
                  {local.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {voiceLabel(v)}
                    </option>
                  ))}
                </optgroup>
              )}
              {online.length > 0 && (
                <optgroup label="Online (sends the text to be spoken)">
                  {online.map((v) => (
                    <option key={v.voiceURI} value={v.voiceURI}>
                      {voiceLabel(v)}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
        </>
      )}
      <button
        type="button"
        className="icon-btn ra-close"
        title="Stop and close (Esc)"
        aria-label="Stop and close"
        onClick={() => {
          closeReadAloud(view);
          view.focus();
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- project definitions

type Definitions = Required<Pick<SpeechContext, "glossary" | "macros">>;
const definitions = new Map<string, { at: number; defs: Promise<Definitions> }>();
const FRESH_MS = 5 * 60_000;

async function loadDefinitions(projectId: string, files: string[]): Promise<Definitions> {
  const into: Definitions = { glossary: new Map(), macros: new Map() };
  const texts = await Promise.all(
    files.map((f) =>
      fetch(`/api/projects/${projectId}/raw?path=${encodeURIComponent(f)}`).then(
        (r) => (r.ok ? r.text() : ""),
        () => "",
      ),
    ),
  );
  for (const t of texts) if (t.length < 2_000_000) collectDefinitions(t, into);
  return into;
}

/**
 * What the reader needs from the rest of the project: acronyms and macros defined in other .tex
 * files (fetched once every few minutes), plus the open file's own, plus citation authors.
 */
export async function speechContext(projectId: string, index: ProjectIndex | null, path: string, text: string): Promise<SpeechContext> {
  const files = (index?.files ?? []).filter((f) => /\.(tex|sty|cls|ltx)$/i.test(f) && f !== path).slice(0, 80);
  let hit = definitions.get(projectId);
  if (!hit || Date.now() - hit.at > FRESH_MS) {
    hit = { at: Date.now(), defs: loadDefinitions(projectId, files) };
    definitions.set(projectId, hit);
  }
  const defs = await hit.defs;
  const merged = collectDefinitions(text, { glossary: new Map(defs.glossary), macros: new Map(defs.macros) });
  const citations = new Map((index?.citations ?? []).map((c) => [c.key, { author: c.author, year: c.year }]));
  return { ...merged, citations };
}
